/**
 * Aspect — MarqueeSelect (left-drag rubber-band multi-selection)
 * --------------------------------------------------------------
 * With a free cursor (pointer not locked), hold the left mouse button and drag
 * on empty space to draw a selection rectangle. Every furniture piece whose
 * screen-projected center falls inside gets selected as a group; the group then
 * follows the cursor (GizmoControls) keeping each item at its own height.
 * Left-click drops the group; right-click / Esc reverts it.
 *
 * A plain click (below the drag threshold) is left untouched so the normal
 * click-to-walk pointer-lock behavior keeps working.
 */

import * as pc from "playcanvas";

const DRAG_THRESHOLD_PX = 7;

export class MarqueeSelect {
  /**
   * @param {pc.AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {import('./picker.js').Picker} picker
   * @param {import('./placement_tool.js').PlacementTool} [placement]
   * @param {import('./measure_tool.js').MeasureTool} [measure]
   */
  constructor(app, camera, scene, picker, placement = null, measure = null) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.picker = picker;
    this.placement = placement;
    this.measure = measure;
    this.canvas = app.graphicsDevice?.canvas ?? null;

    this._down = null; // {x, y}
    this._dragging = false;
    this._rectEl = null;
    this._overhead = false;
    this._suppressNextClick = false;

    this.app.on("cam:mode-changed", ({ mode }) => {
      this._overhead = mode === "overhead";
    });

    if (this.canvas) {
      this.canvas.addEventListener("mousedown", (e) => this._onDown(e));
      window.addEventListener("mousemove", (e) => this._onMove(e));
      window.addEventListener("mouseup", (e) => this._onUp(e));
      // Swallow the click generated by a completed marquee drag so the
      // first-person controls don't re-engage pointer lock.
      this.canvas.addEventListener(
        "click",
        (e) => {
          if (this._suppressNextClick) {
            this._suppressNextClick = false;
            e.stopImmediatePropagation();
            e.preventDefault();
          }
        },
        { capture: true },
      );
    }
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this._dragging) this._cancelDrag();
    });
  }

  _pointerLocked() {
    return this.canvas && document.pointerLockElement === this.canvas;
  }

  _allowed() {
    if (!this.canvas || this._pointerLocked()) return false;
    if (this._overhead) return false; // structure marquee owns overhead drags
    if (this.placement?.active) return false;
    if (this.measure?.active) return false;
    if (document.body.classList.contains("plan-editor-open")) return false;
    if (this.picker?.disableSelect || this.picker?.readOnly) return false;
    return true;
  }

  _onDown(e) {
    if (e.button !== 0 || !this._allowed()) return;
    if (e.target !== this.canvas) return;
    // A group is already selected: this click commits it (GizmoControls hears
    // the same mousedown through picker/gizmo flow) — don't start a marquee.
    if (this.picker.selectedIds?.size) return;
    this._down = { x: e.clientX, y: e.clientY };
    this._dragging = false;
  }

  _onMove(e) {
    if (!this._down) return;
    const dx = e.clientX - this._down.x;
    const dy = e.clientY - this._down.y;
    if (!this._dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      this._dragging = true;
      this._showRect();
    }
    this._updateRect(e.clientX, e.clientY);
  }

  _onUp(e) {
    if (!this._down) return;
    const start = this._down;
    const wasDragging = this._dragging;
    this._down = null;
    this._dragging = false;
    this._hideRect();
    if (!wasDragging) return;

    this._suppressNextClick = true;
    const x0 = Math.min(start.x, e.clientX);
    const x1 = Math.max(start.x, e.clientX);
    const y0 = Math.min(start.y, e.clientY);
    const y1 = Math.max(start.y, e.clientY);
    if (x1 - x0 < 4 && y1 - y0 < 4) return;

    const ids = this._idsInScreenRect(x0, y0, x1, y1);
    if (ids.length === 1) {
      this.picker.select(ids[0]);
    } else if (ids.length > 1) {
      this.picker.selectMany(ids);
    }
  }

  _cancelDrag() {
    this._down = null;
    this._dragging = false;
    this._hideRect();
  }

  /** Furniture ids whose projected AABB center lies inside the screen rect. */
  _idsInScreenRect(x0, y0, x1, y1) {
    const cam = this.camera?.camera;
    if (!cam) return [];
    const rect = this.canvas.getBoundingClientRect();
    const out = [];
    const screen = new pc.Vec3();
    for (const [id, entity] of this.scene.entities) {
      if (!entity?.enabled) continue;
      const pos = entity.getPosition();
      cam.worldToScreen(pos, screen);
      if (screen.z < 0) continue; // behind the camera
      const sx = rect.left + screen.x;
      const sy = rect.top + screen.y;
      if (sx >= x0 && sx <= x1 && sy >= y0 && sy <= y1) out.push(id);
    }
    return out;
  }

  _showRect() {
    if (this._rectEl) return;
    const el = document.createElement("div");
    el.style.cssText = [
      "position:fixed",
      "z-index:60",
      "border:1.5px solid rgba(93,156,255,0.9)",
      "background:rgba(93,156,255,0.14)",
      "border-radius:4px",
      "pointer-events:none",
    ].join(";");
    document.body.appendChild(el);
    this._rectEl = el;
  }

  _updateRect(cx, cy) {
    if (!this._rectEl || !this._down) return;
    const x = Math.min(this._down.x, cx);
    const y = Math.min(this._down.y, cy);
    const w = Math.abs(cx - this._down.x);
    const h = Math.abs(cy - this._down.y);
    Object.assign(this._rectEl.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
  }

  _hideRect() {
    this._rectEl?.remove();
    this._rectEl = null;
  }

  destroy() {
    this._hideRect();
  }
}

export default MarqueeSelect;
